/**
 * A match, its seven bet lines, and the signals.
 *
 * **This is where the rules become pixels, so they are worth restating where they are applied:**
 *
 * - The bet name **bolds** on `isGoodBet` = `(suggested || hasEdge) && !historyContradicts` — never
 *   on `suggested` alone, which fires about once in 45 bets and left real edges unhighlighted.
 * - The AI win % turns **amber** on `hasEdge` = AI% − market% ≥ 5 points. That is the only signal
 *   that makes money.
 * - A high win % on its own gets no emphasis at all. Deliberately: a bet the model rates below the
 *   market price is a bad bet however likely it is to land.
 * - Market % has its own column, because the comparison is the whole point.
 * - ▲ is the history-only propensity flag, and it only appears once a prediction exists — it is
 *   computed from team history and would otherwise mark up empty cards, which reads as a
 *   recommendation the app has not made.
 *
 * **Place records an intent, it does not sign an order.** The Ed25519 money key lives on the PC and
 * never leaves it, so the phone asks and `trader/` executes.
 *
 * The card is layout-agnostic: it never reads the window width. The screen arranges it.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import {
  betList,
  canPlaceBet,
  convictionEdge,
  edge,
  hasEdge,
  isGoodBet,
  isHighConviction,
  kickoffDisplay,
  pct,
  profileDisplay,
  relativeKickoff,
  showProfileFlag,
  signed,
  BET_SHORT,
  type BetKey,
  type BetLine,
  type Match,
} from '@/models';
import * as React from 'react';
import { Pressable, View } from 'react-native';

interface Props {
  match: Match;
  /** Per-line stakes, keyed by bet key. "Apply to all" writes into every visible one. */
  stakes: Partial<Record<BetKey, string>>;
  onStake: (key: BetKey, value: string) => void;
  onPredict: () => void;
  onPlace: (key: BetKey) => void;
  /** Status of the last placement attempt on this card, keyed by bet. */
  placement?: Partial<Record<BetKey, string>>;
  /** Narrow layouts show only the lines carrying something; wide shows all seven. */
  dense?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export function MatchCard({
  match,
  stakes,
  onStake,
  onPredict,
  onPlace,
  placement,
  dense = false,
  expanded = false,
  onToggleExpand,
}: Props) {
  const lines = betList(match);
  const profile = profileDisplay(match.prediction);
  const queued = match.jobStatus === 'queued' || match.jobStatus === 'running';

  // On a narrow card only the lines carrying something worth reading are shown; the rest are noise
  // on a phone. Expanding shows all seven.
  const shown =
    dense && !expanded
      ? lines.filter((b) => b.aiPercent !== null || b.profileFlagged || b.marketPercent !== null)
      : lines;

  return (
    <Card>
      <CardHeader className="gap-1">
        <Pressable onPress={onToggleExpand} accessibilityRole="button">
          <View className="flex-row items-start justify-between gap-3">
            <View className="min-w-0 flex-1 shrink">
              <Text variant="heading" numberOfLines={2}>
                {match.homeTeam} v {match.awayTeam}
              </Text>
              {/* One line, truncated. The venue's market count lives here rather than in the
                  corner: as a second caption it had no width to shrink into and spilled past the
                  card's own edge at every card width. */}
              <Text variant="small" className="text-muted-foreground" numberOfLines={1}>
                {match.league} · {kickoffDisplay(match.kickoffUtc)} ·{' '}
                {relativeKickoff(match.kickoffUtc)}
                {match.marketCount > 0 ? ` · ${match.marketCount} markets` : ''}
              </Text>
            </View>
            <View className="shrink-0 items-end">
              {match.prediction ? (
                <Badge variant="default">
                  <Text>{match.prediction.expectedTotalGoals.toFixed(1)} xG</Text>
                </Badge>
              ) : queued ? (
                <Badge variant="secondary">
                  <Text>{match.jobStatus === 'running' ? 'Running' : 'Queued'}</Text>
                </Badge>
              ) : null}
            </View>
          </View>
        </Pressable>
      </CardHeader>

      <CardContent className="gap-2 py-3">
        {/* The column header only makes sense over the single-line arrangement. A phone gets the
            stacked one, where each value carries its own label. */}
        {dense ? null : (
          <View className="flex-row items-center gap-2 pb-1">
            <Text variant="caption" className="text-muted-foreground flex-1">
              Bet
            </Text>
            <Text variant="caption" className="text-muted-foreground w-11 text-right">
              AI
            </Text>
            <Text variant="caption" className="text-muted-foreground w-16 text-right">
              Market
            </Text>
            <Text variant="caption" className="text-muted-foreground w-14 text-right">
              League
            </Text>
            <Text variant="caption" className="text-muted-foreground w-14 text-right">
              Stake
            </Text>
            <View className="w-14" />
          </View>
        )}

        {shown.map((b) => (
          <BetRow
            key={b.key}
            bet={b}
            dense={dense}
            stake={stakes[b.key] ?? ''}
            onStake={(v) => onStake(b.key, v)}
            onPlace={() => onPlace(b.key)}
            status={placement?.[b.key]}
          />
        ))}

        {match.modelInfo ? (
          <Text variant="caption" className="text-muted-foreground pt-1">
            {match.modelInfo}
          </Text>
        ) : null}

        {match.jobStatus === 'failed' && match.jobError ? (
          <Text variant="small" className="text-destructive">
            Prediction failed: {match.jobError}
          </Text>
        ) : null}

        {queued && !match.prediction ? (
          <Text variant="small" className="text-muted-foreground">
            Queued for the AI gateway. It runs on the PC, so this waits while the PC is asleep.
          </Text>
        ) : null}

        {expanded && match.prediction ? (
          <View className="gap-1 pt-1">
            {profile ? (
              <Text variant="small" className="text-muted-foreground">
                {profile}
              </Text>
            ) : null}
            {match.prediction.topScores.length ? (
              <Text variant="small" className="text-muted-foreground">
                Likely scores: {match.prediction.topScores.join('   ')}
              </Text>
            ) : null}
            <Text variant="small">{match.prediction.explanation}</Text>
          </View>
        ) : null}

        <View className="flex-row items-center gap-2 pt-1">
          <Button
            variant={match.prediction ? 'outline' : 'default'}
            size="sm"
            disabled={queued}
            onPress={onPredict}>
            <Text>{queued ? 'Queued…' : match.prediction ? 'Re-run' : 'Run prediction'}</Text>
          </Button>
          {onToggleExpand ? (
            <Button variant="ghost" size="sm" onPress={onToggleExpand}>
              <Text>{expanded ? 'Less' : 'More'}</Text>
            </Button>
          ) : null}
        </View>
      </CardContent>
    </Card>
  );
}


/**
 * One bet line.
 *
 * Two arrangements over one body of state, chosen by the `dense` flag the SCREEN passes down —
 * the component still never reads the window width. Wide is a table row, because the comparison
 * between the AI number and the market number is the point and columns make it scannable. Narrow
 * stacks the values under the name with their own labels, because six fixed columns plus an input
 * and a button do not fit in 324px and squeezing them turned every bet name into a single letter.
 */
function BetRow({
  bet,
  dense,
  stake,
  onStake,
  onPlace,
  status,
}: {
  bet: BetLine;
  dense: boolean;
  stake: string;
  onStake: (v: string) => void;
  onPlace: () => void;
  status?: string;
}) {
  const good = isGoodBet(bet);
  const value = hasEdge(bet);
  const e = edge(bet);
  const conviction = convictionEdge(bet);
  const tradable = canPlaceBet(bet);
  const staked = Number.parseFloat(stake);

  const name = (
    <View className="min-w-0 flex-row items-center gap-1">
      <Text
        variant="small"
        numberOfLines={1}
        className={cn(good ? 'text-foreground font-semibold' : 'text-muted-foreground')}>
        {dense ? bet.name : BET_SHORT[bet.key]}
      </Text>
      {showProfileFlag(bet) ? (
        <Text variant="caption" className="text-primary">
          ▲
        </Text>
      ) : null}
    </View>
  );

  // Amber is VALUE and only value. A high percentage with no edge stays plain on purpose.
  const ai = (
    <Text
      variant="small"
      className={cn(value ? 'text-warning font-semibold' : 'text-foreground')}>
      {pct(bet.aiPercent)}
    </Text>
  );

  const stakeBox = (
    <Input
      className="native:h-8 h-8 w-14 px-2 text-right"
      value={stake}
      onChangeText={onStake}
      placeholder="—"
      keyboardType="decimal-pad"
      editable={tradable}
      aria-label={`Stake for ${bet.name}`}
    />
  );

  const placeButton = (
    <Button
      variant="secondary"
      size="sm"
      className="w-14"
      disabled={!tradable || !(staked > 0)}
      onPress={onPlace}>
      <Text>{tradable ? 'Place' : '—'}</Text>
    </Button>
  );

  if (dense) {
    return (
      <View className="border-border gap-1 border-t py-1.5">
        <View className="flex-row items-center justify-between gap-2">
          {name}
          <View className="flex-row items-center gap-2">
            {stakeBox}
            {placeButton}
          </View>
        </View>
        <View className="flex-row flex-wrap items-baseline gap-x-3">
          <Labelled label="AI">{ai}</Labelled>
          <Labelled label="Market">
            <Text variant="small" className="text-muted-foreground">
              {pct(bet.marketPercent)}
            </Text>
            {value && e !== null ? (
              <Text variant="caption" className="text-warning">
                {' '}
                {signed(e)}
              </Text>
            ) : null}
          </Labelled>
          <Labelled label="League">
            <Text variant="small" className="text-muted-foreground">
              {pct(bet.leaguePercent)}
            </Text>
            {isHighConviction(bet) && conviction !== null ? (
              <Text variant="caption" className="text-muted-foreground">
                {' '}
                {signed(conviction)}
              </Text>
            ) : null}
          </Labelled>
        </View>
        {status ? (
          <Text variant="caption" className="text-muted-foreground">
            {status}
          </Text>
        ) : null}
      </View>
    );
  }

  return (
    <View className="gap-0.5">
      <View className="flex-row items-center gap-2 py-0.5">
        <View className="min-w-0 flex-1">{name}</View>
        <View className="w-11 items-end">{ai}</View>
        <View className="w-16 flex-row items-baseline justify-end gap-1">
          <Text variant="small" className="text-muted-foreground">
            {pct(bet.marketPercent)}
          </Text>
          {value && e !== null ? (
            <Text variant="caption" className="text-warning">
              {signed(e)}
            </Text>
          ) : null}
        </View>
        <View className="w-14 flex-row items-baseline justify-end gap-1">
          <Text variant="small" className="text-muted-foreground">
            {pct(bet.leaguePercent)}
          </Text>
          {isHighConviction(bet) && conviction !== null ? (
            <Text variant="caption" className="text-muted-foreground">
              {signed(conviction)}
            </Text>
          ) : null}
        </View>
        {stakeBox}
        {placeButton}
      </View>

      {status ? (
        <Text variant="caption" className="text-muted-foreground pl-1">
          {status}
        </Text>
      ) : null}
    </View>
  );
}

/** A value with its own caption, for the stacked arrangement where there is no column header. */
function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View className="flex-row items-baseline gap-1">
      <Text variant="caption" className="text-muted-foreground uppercase">
        {label}
      </Text>
      {children}
    </View>
  );
}
